import React, { useState, useEffect } from 'react';
import './MessageHistory.css';

function MessageHistory({ messages, isOpen, onClose, onClearHistory, onSpeak, activeMessageId }) {
    const [selectedDate, setSelectedDate] = useState(null);
    const [dateToDelete, setDateToDelete] = useState(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    // Grouping logic: organize messages by day
    const groupedMessages = messages.reduce((groups, msg) => {
        try {
            const date = msg.timestamp instanceof Date ? msg.timestamp : new Date(msg.timestamp);
            const dateKey = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
            if (!groups[dateKey]) groups[dateKey] = [];
            groups[dateKey].push(msg);
        } catch (e) {
            const fallbackKey = "Older Conversations";
            if (!groups[fallbackKey]) groups[fallbackKey] = [];
            groups[fallbackKey].push(msg);
        }
        return groups;
    }, {});

    // Helper to get formatted date labels (Today, Yesterday, etc.)
    const getDateLabel = (dateStr) => {
        if (dateStr === "Older Conversations") return dateStr;
        const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        const yesterday = new Date(Date.now() - 86400000).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        
        if (dateStr === today) return "Today";
        if (dateStr === yesterday) return "Yesterday";
        return dateStr;
    };

    // Sort dates by descending time
    const sortedDates = Object.keys(groupedMessages).sort((a, b) => {
        if (a === "Older Conversations") return 1;
        if (b === "Older Conversations") return -1;
        return new Date(b) - new Date(a);
    });

    // Reset view when closing
    useEffect(() => {
        if (!isOpen) {
            setSelectedDate(null);
        }
    }, [isOpen]);

    const handleClearHistory = () => {
        if (dateToDelete) {
            onClearHistory(dateToDelete);
        } else {
            onClearHistory();
        }
        setShowConfirmDialog(false);
        setDateToDelete(null);
        if (selectedDate === dateToDelete) {
            setSelectedDate(null);
        }
    };

    const confirmDeleteDate = (e, date) => {
        e.stopPropagation();
        setDateToDelete(date);
        setShowConfirmDialog(true);
    };

    const confirmClearAll = () => {
        setDateToDelete(null);
        setShowConfirmDialog(true);
    };

    const formatMessage = (text) => {
        if (!text) return null;

        // 1. Split into Sections
        // Find the Shloka Citation as the primary divider (supports English, Hindi, and Tamil)
        const shlokaCitationMatch = text.match(/((?:Bhagavad Gita|भगवद गीता|பகவத் கீதை),?\s*(?:Chapter|अध्याय|அத்தியாயம்)\s*\d+,?\s*(?:Shloka|श्लोक|ஸ்லோகம்)\s*\d+)/i);
        
        if (!shlokaCitationMatch) {
            return <div className="response-box general-box"><p>{text}</p></div>;
        }

        const citation = shlokaCitationMatch[0];
        const splitIndex = text.indexOf(citation);
        
        // Part A: Everything before citation (Introduction)
        const intro = text.substring(0, splitIndex).trim();
        
        // Part B: The rest starting from citation
        const rest = text.substring(splitIndex + citation.length).trim();

        // Further split 'rest' into Shloka text, Explanation, and Steps
        const stepsMarker = rest.match(/(?:Step 1|कदम 1|படி 1):/i);
        let shlokaAndExplanation = rest;
        let stepsPart = "";

        if (stepsMarker) {
            const stepsIndex = rest.indexOf(stepsMarker[0]);
            shlokaAndExplanation = rest.substring(0, stepsIndex).trim();
            stepsPart = rest.substring(stepsIndex).trim();
        }

        // Shloka text usually contains ॥
        const shlokaEndMarker = shlokaAndExplanation.indexOf('॥');
        let shlokaText = "";
        let explanation = shlokaAndExplanation;

        if (shlokaEndMarker !== -1) {
            shlokaText = shlokaAndExplanation.substring(0, shlokaEndMarker + 1).trim();
            explanation = shlokaAndExplanation.substring(shlokaEndMarker + 1).trim();
        } else {
            const lines = shlokaAndExplanation.split('\n');
            if (lines.length > 2) {
                shlokaText = lines.slice(0, 2).join('\n');
                explanation = lines.slice(2).join('\n');
            }
        }

        const isTamil = text.match(/[\u0B80-\u0BFF]/);
        const isEnglish = text.match(/[a-z]{5,}/); // 5 or more lowercase chars usually indicates English prose, avoiding shloka false positives

        const getBoxTitle = (key) => {
            if (isTamil) {
                switch(key) {
                    case 'intro': return 'திவ்ய செய்தி';
                    case 'shloka': return 'புனித ஸ்லோகம்';
                    case 'explanation': return 'ஆழ்ந்த விளக்கம்';
                    case 'steps': return 'உங்களுக்கான வழி';
                    default: return '';
                }
            }
            if (isEnglish) {
                switch(key) {
                    case 'intro': return 'Divine Message';
                    case 'shloka': return 'Sacred Shloka';
                    case 'explanation': return 'Deeper Meaning';
                    case 'steps': return 'Path For You';
                    default: return '';
                }
            }
            // Default to Hindi
            switch(key) {
                case 'intro': return 'दिव्य संदेश';
                case 'shloka': return 'पवित्र श्लोक';
                case 'explanation': return 'गहन व्याख्या';
                case 'steps': return 'आपके लिए मार्ग';
                default: return '';
            }
        };

        return (
            <div className="response-boxes">
                {/* 1. Introduction */}
                {intro && (
                    <div className="response-box general-box">
                        <span className="box-title">{getBoxTitle('intro')}</span>
                        <p>{intro}</p>
                    </div>
                )}

                {/* 2. Shloka */}
                <div className="response-box shloka-box">
                    <span className="box-title">{getBoxTitle('shloka')}</span>
                    <div className="shloka-content">
                        <div className="citation-label">{citation}</div>
                        <p>{shlokaText}</p>
                    </div>
                </div>

                {/* 3. Explanation */}
                {explanation && (
                    <div className="response-box explanation-box">
                        <span className="box-title">{getBoxTitle('explanation')}</span>
                        <p>{explanation}</p>
                    </div>
                )}

                {/* 4. Action Steps */}
                {stepsPart && (
                    <div className="response-box steps-box">
                        <span className="box-title">{getBoxTitle('steps')}</span>
                        <p>{stepsPart}</p>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <div className={`history-overlay ${isOpen ? 'show' : ''}`} onClick={onClose} />
            <div className={`message-history ${isOpen ? 'open' : ''}`}>
                <div className="history-header">
                    {selectedDate ? (
                        <div className="header-nav">
                            <button className="back-header-button" onClick={() => setSelectedDate(null)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <span>Back</span>
                            </button>
                            <h2>{getDateLabel(selectedDate)}</h2>
                        </div>
                    ) : (
                        <>
                            <h2>Dialogue History</h2>
                            <div className="header-actions">
                                <button className="clear-history-button" onClick={confirmClearAll} title="Clear all history">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                                <button className="close-button" onClick={onClose} title="Close">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                            </div>
                        </>
                    )}
                </div>

                <div className="history-content">
                    {messages.length === 0 ? (
                        <div className="empty-state">
                            Your journey has just begun. Start a conversation to see it here.
                        </div>
                    ) : selectedDate ? (
                        <div className="date-messages">
                            {[...groupedMessages[selectedDate]].reverse().map((msg, idx) => (
                                <div key={idx} className="history-interaction">
                                    <div className="history-message user">
                                        <div className="message-header">
                                            <span className="message-author">You</span>
                                            <span className="message-time">
                                                {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>
                                        <div className="message-body">
                                            <p>{msg.question || "(Welcome Message)"}</p>
                                        </div>
                                    </div>

                                    <div className="history-message krishna">
                                        <div className="message-header">
                                            <span className="message-author">Sri Krishna</span>
                                            <button 
                                                className={`speak-button ${activeMessageId === (msg.id || msg.timestamp) ? 'speaking' : ''}`}
                                                onClick={() => onSpeak(msg.answer, msg.id || msg.timestamp)}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M11 5L6 9H2v6h4l5 4V5zM19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </button>
                                        </div>
                                        <div className="message-body">
                                            {formatMessage(msg.answer)}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="date-list">
                            {sortedDates.map((date) => (
                                <div 
                                    key={date} 
                                    className="date-item" 
                                    onClick={() => setSelectedDate(date)}
                                >
                                    <div className="date-item-info">
                                        <div className="date-icon">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                <line x1="16" y1="2" x2="16" y2="6" />
                                                <line x1="8" y1="2" x2="8" y2="6" />
                                                <line x1="3" y1="10" x2="21" y2="10" />
                                            </svg>
                                        </div>
                                        <div className="date-text">
                                            <h4>{getDateLabel(date)}</h4>
                                            <span>{groupedMessages[date].length} dialogue records</span>
                                        </div>
                                    </div>
                                    <div className="date-item-actions">
                                        <button 
                                            className="date-delete-btn" 
                                            onClick={(e) => confirmDeleteDate(e, date)}
                                            title="Delete this day's history"
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="3 6 5 6 21 6"></polyline>
                                                <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6m5 0V4a2 2 0 012-2h2a2 2 0 012 2v2"></path>
                                            </svg>
                                        </button>
                                        <div className="date-chevron">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Confirmation Dialog */}
            {showConfirmDialog && (
                <div className="confirm-overlay" onClick={() => setShowConfirmDialog(false)}>
                    <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="confirm-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <h3>{dateToDelete ? `Delete ${getDateLabel(dateToDelete)} History?` : "Clear All History?"}</h3>
                        <p>This action will permanently remove these records from your journey. This cannot be undone.</p>
                        <div className="confirm-actions">
                            <button className="btn-cancel" onClick={() => setShowConfirmDialog(false)}>Cancel</button>
                            <button className="btn-confirm" onClick={handleClearHistory}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default React.memo(MessageHistory);
